/**
 * Pipelines Domain Seed Data
 *
 * Contains all seed data for Pipeline configurations:
 * - Pipelines (workflow definitions)
 * - Pipeline Stages (workflow steps)
 *
 * Pipelines define workflows for case management, loan processing,
 * document review, and other business processes.
 */

import { seedId } from '../utils';
import { BUSINESS_IDS } from './tenants';

// =============================================================================
// PIPELINE IDs
// =============================================================================

export const PIPELINE_IDS = {
  /** Loan Application Pipeline - standard loan processing workflow */
  LOAN_APPLICATION: seedId('pipeline-loan-app', 1),
  /** Support Ticket Pipeline - customer support workflow */
  SUPPORT_TICKET: seedId('pipeline-support', 2),
  /** Document Review Pipeline - document processing workflow */
  DOCUMENT_REVIEW: seedId('pipeline-doc-review', 3),
  /** Payment Processing Pipeline - payment handling workflow */
  PAYMENT_PROCESSING: seedId('pipeline-payment', 4),
} as const;

export type PipelineId = (typeof PIPELINE_IDS)[keyof typeof PIPELINE_IDS];

// =============================================================================
// PIPELINE STAGE IDs
// =============================================================================

export const PIPELINE_STAGE_IDS = {
  // Loan Application Pipeline Stages
  LOAN_NEW_APPLICATION: seedId('stage-loan-new', 1),
  LOAN_DOCUMENT_COLLECTION: seedId('stage-loan-docs', 2),
  LOAN_UNDERWRITING: seedId('stage-loan-uw', 3),
  LOAN_PENDING_APPROVAL: seedId('stage-loan-pending', 4),
  LOAN_CLEAR_TO_CLOSE: seedId('stage-loan-ctc', 5),
  LOAN_FUNDED: seedId('stage-loan-funded', 6),
  LOAN_DENIED: seedId('stage-loan-denied', 7),
  LOAN_WITHDRAWN: seedId('stage-loan-withdrawn', 8),

  // Support Ticket Pipeline Stages
  SUPPORT_NEW: seedId('stage-support-new', 1),
  SUPPORT_IN_PROGRESS: seedId('stage-support-ip', 2),
  SUPPORT_WAITING: seedId('stage-support-waiting', 3),
  SUPPORT_ESCALATED: seedId('stage-support-escalated', 4),
  SUPPORT_RESOLVED: seedId('stage-support-resolved', 5),
  SUPPORT_CLOSED: seedId('stage-support-closed', 6),

  // Document Review Pipeline Stages
  DOC_SUBMITTED: seedId('stage-doc-submitted', 1),
  DOC_AI_PROCESSING: seedId('stage-doc-ai', 2),
  DOC_HUMAN_REVIEW: seedId('stage-doc-human', 3),
  DOC_APPROVED: seedId('stage-doc-approved', 4),
  DOC_REJECTED: seedId('stage-doc-rejected', 5),

  // Payment Processing Pipeline Stages
  PAYMENT_RECEIVED: seedId('stage-payment-received', 1),
  PAYMENT_UNDER_REVIEW: seedId('stage-payment-review', 2),
  PAYMENT_PENDING_APPROVAL: seedId('stage-payment-pending', 3),
  PAYMENT_PROCESSING: seedId('stage-payment-processing', 4),
  PAYMENT_COMPLETED: seedId('stage-payment-completed', 5),
  PAYMENT_FAILED: seedId('stage-payment-failed', 6),
} as const;

export type PipelineStageId = (typeof PIPELINE_STAGE_IDS)[keyof typeof PIPELINE_STAGE_IDS];

// =============================================================================
// PIPELINE AUTOMATION IDs
// =============================================================================

export const PIPELINE_AUTOMATION_IDS = {
  // Loan Application Automations
  LOAN_AUTO_ASSIGN_UNDERWRITER: seedId('automation-loan-assign-uw', 1),
  LOAN_AUTO_NOTIFY_BORROWER: seedId('automation-loan-notify', 2),
  LOAN_SLA_ESCALATION: seedId('automation-loan-sla', 3),

  // Support Ticket Automations
  SUPPORT_AUTO_CATEGORIZE: seedId('automation-support-categorize', 1),
  SUPPORT_URGENT_ESCALATION: seedId('automation-support-urgent', 2),
  SUPPORT_AUTO_CLOSE_RESOLVED: seedId('automation-support-close', 3),

  // Document Review Automations
  DOC_AUTO_OCR_TRIGGER: seedId('automation-doc-ocr', 1),
  DOC_CONFIDENCE_ROUTING: seedId('automation-doc-confidence', 2),

  // Payment Processing Automations
  PAYMENT_LARGE_AMOUNT_APPROVAL: seedId('automation-payment-large', 1),
  PAYMENT_RETRY_FAILED: seedId('automation-payment-retry', 2),
} as const;

export type PipelineAutomationId = (typeof PIPELINE_AUTOMATION_IDS)[keyof typeof PIPELINE_AUTOMATION_IDS];

// =============================================================================
// SLA DEFINITION IDs
// =============================================================================

export const SLA_DEFINITION_IDS = {
  // Loan SLAs (OpsMatrix Capital)
  LOAN_INITIAL_REVIEW: seedId('sla-loan-initial', 1),
  LOAN_UNDERWRITING_REVIEW: seedId('sla-loan-uw', 2),
  LOAN_APPROVAL_TURNAROUND: seedId('sla-loan-approval', 3),
  LOAN_TOTAL_PROCESSING: seedId('sla-loan-total', 4),

  // Support SLAs (OpsMatrix Capital)
  SUPPORT_FIRST_RESPONSE: seedId('sla-support-first', 1),
  SUPPORT_RESOLUTION_TIME: seedId('sla-support-resolution', 2),
  SUPPORT_ESCALATION_RESPONSE: seedId('sla-support-escalation', 3),

  // Document SLAs (OpsMatrix Capital)
  DOC_AI_PROCESSING: seedId('sla-doc-ai', 1),
  DOC_HUMAN_REVIEW: seedId('sla-doc-human', 2),

  // Payment SLAs (OpsMatrix Capital)
  PAYMENT_PROCESSING_TIME: seedId('sla-payment-processing', 1),

  // 1Sharpe Capital Construction SLAs
  SHARPE_CONSTRUCTION_DRAW: seedId('sla-sharpe-draw', 1),
  SHARPE_INSPECTION_REVIEW: seedId('sla-sharpe-inspection', 2),
  SHARPE_CONTRACTOR_APPROVAL: seedId('sla-sharpe-contractor', 3),

  // Pacific Bridge Fund SLAs
  PACIFIC_INVESTMENT_COMMITTEE: seedId('sla-pacific-invest', 1),
  PACIFIC_TERM_SHEET_ISSUANCE: seedId('sla-pacific-termsheet', 2),
} as const;

export type SLADefinitionId = (typeof SLA_DEFINITION_IDS)[keyof typeof SLA_DEFINITION_IDS];

// =============================================================================
// SLA TRACKER IDs
// =============================================================================

export const SLA_TRACKER_IDS = {
  // OpsMatrix Capital SLA trackers
  MARCUS_LOAN_SLA: seedId('sla-tracker-marcus', 1),
  DAVID_SUPPORT_SLA: seedId('sla-tracker-david', 1),
  JESSICA_DOC_SLA: seedId('sla-tracker-jessica', 1),

  // 1Sharpe Capital SLA trackers
  CHRISTOPHER_PAYMENT_SLA: seedId('sla-tracker-chris', 1),
  MARIA_CONSTRUCTION_SLA: seedId('sla-tracker-maria-const', 1),
  SHARPE_DRAW_SLA: seedId('sla-tracker-sharpe-draw', 1),

  // Pacific Bridge Fund SLA trackers
  PACIFIC_COMMITTEE_SLA: seedId('sla-tracker-pacific-committee', 1),
} as const;

export type SLATrackerId = (typeof SLA_TRACKER_IDS)[keyof typeof SLA_TRACKER_IDS];

// =============================================================================
// AUTOMATION TRIGGER TYPES
// =============================================================================

export const AUTOMATION_TRIGGERS = {
  STAGE_ENTERED: 'STAGE_ENTERED',
  STAGE_EXITED: 'STAGE_EXITED',
  FIELD_CHANGED: 'FIELD_CHANGED',
  RECORD_CREATED: 'RECORD_CREATED',
  RECORD_UPDATED: 'RECORD_UPDATED',
  SLA_WARNING: 'SLA_WARNING',
  SLA_BREACH: 'SLA_BREACH',
  SCHEDULED: 'SCHEDULED',
} as const;

export type AutomationTrigger = (typeof AUTOMATION_TRIGGERS)[keyof typeof AUTOMATION_TRIGGERS];

// =============================================================================
// SLA STATUS TYPES
// =============================================================================

export const SLA_STATUSES = {
  ON_TRACK: 'ON_TRACK',
  AT_RISK: 'AT_RISK',
  BREACHED: 'BREACHED',
  COMPLETED: 'COMPLETED',
  PAUSED: 'PAUSED',
} as const;

export type SLAStatus = (typeof SLA_STATUSES)[keyof typeof SLA_STATUSES];

// =============================================================================
// SLA METRIC TYPES
// =============================================================================

export const SLA_METRIC_TYPES = {
  TIME_IN_STAGE: 'TIME_IN_STAGE',
  TIME_TO_COMPLETION: 'TIME_TO_COMPLETION',
  FIRST_RESPONSE_TIME: 'FIRST_RESPONSE_TIME',
  TOTAL_RESOLUTION_TIME: 'TOTAL_RESOLUTION_TIME',
  CUSTOM: 'CUSTOM',
} as const;

export type SLAMetricType = (typeof SLA_METRIC_TYPES)[keyof typeof SLA_METRIC_TYPES];

// =============================================================================
// STAGE TYPES
// =============================================================================

export const STAGE_TYPES = {
  INITIAL: 'INITIAL',
  NORMAL: 'NORMAL',
  WAITING: 'WAITING',
  APPROVAL: 'APPROVAL',
  SUCCESS: 'SUCCESS',
  FAILURE: 'FAILURE',
} as const;

export type StageType = (typeof STAGE_TYPES)[keyof typeof STAGE_TYPES];

// =============================================================================
// DATA TYPES
// =============================================================================

export interface PipelineStageSeedData {
  id: string;
  name: string;
  description?: string;
  icon?: string;
  color: string;
  stageType: StageType;
  order: number;
  pauseSla?: boolean;
  slaHours?: number;
  requiredFields?: string[];
  isActive: boolean;
}

export interface PipelineSeedData {
  id: string;
  name: string;
  description: string;
  icon?: string;
  color?: string;
  entityTypes: string[];
  defaultSlaHours?: number;
  isActive: boolean;
  sortOrder: number;
  stages: PipelineStageSeedData[];
}

export interface PipelineAutomationSeedData {
  id: string;
  businessId: string;
  pipelineId: string;
  name: string;
  description?: string;
  triggerType: AutomationTrigger;
  triggerConfig: Record<string, unknown>;
  conditions: Record<string, unknown>;
  actions: Array<{ type: string; [key: string]: unknown }>;
  isActive: boolean;
  priority: number;
}

export interface SLADefinitionSeedData {
  id: string;
  businessId: string;
  name: string;
  description?: string;
  entityTypes: string[];
  pipelineId?: string;
  stageId?: string;
  metricType: SLAMetricType;
  customFormula?: string;
  targetValue: number; // in minutes
  warningThreshold: number; // percent of target
  criticalThreshold: number; // percent of target
  useBusinessHours: boolean;
  businessHoursId?: string;
  priority: number;
  isActive: boolean;
}

export interface SLATrackerSeedData {
  id: string;
  businessId: string;
  entityType: string;
  entityId: string;
  pipelineId: string;
  currentStageId: string;
  startedAt: string;
  pausedAt?: string;
  pausedDuration: number;
  dueAt: string;
  status: SLAStatus;
  breachedAt?: string;
  escalationLevel: number;
  lastEscalatedAt?: string;
  stageHistory: Array<{ stageId: string; enteredAt: string; exitedAt?: string; duration?: number }>;
}

// =============================================================================
// PIPELINE DATA
// =============================================================================

export const PIPELINES: Record<keyof typeof PIPELINE_IDS, PipelineSeedData> = {
  // ==========================================================================
  // LOAN APPLICATION PIPELINE
  // ==========================================================================
  LOAN_APPLICATION: {
    id: PIPELINE_IDS.LOAN_APPLICATION,
    name: 'Loan Application Pipeline',
    description: 'Standard workflow for processing loan applications from submission to funding',
    icon: 'file-text',
    color: '#3B82F6', // Blue
    entityTypes: ['loan_application', 'case'],
    defaultSlaHours: 72,
    isActive: true,
    sortOrder: 1,
    stages: [
      {
        id: PIPELINE_STAGE_IDS.LOAN_NEW_APPLICATION,
        name: 'New Application',
        description: 'Application submitted, awaiting initial review',
        icon: 'inbox',
        color: '#6B7280', // Gray
        stageType: STAGE_TYPES.INITIAL,
        order: 1,
        slaHours: 4,
        isActive: true,
      },
      {
        id: PIPELINE_STAGE_IDS.LOAN_DOCUMENT_COLLECTION,
        name: 'Document Collection',
        description: 'Gathering required documentation from borrower',
        icon: 'folder-open',
        color: '#F59E0B', // Amber
        stageType: STAGE_TYPES.WAITING,
        order: 2,
        pauseSla: true, // SLA paused while waiting for borrower
        requiredFields: ['income_docs', 'identity_docs'],
        isActive: true,
      },
      {
        id: PIPELINE_STAGE_IDS.LOAN_UNDERWRITING,
        name: 'Underwriting Review',
        description: 'Loan being reviewed by underwriting team',
        icon: 'clipboard-check',
        color: '#8B5CF6', // Purple
        stageType: STAGE_TYPES.NORMAL,
        order: 3,
        slaHours: 24,
        isActive: true,
      },
      {
        id: PIPELINE_STAGE_IDS.LOAN_PENDING_APPROVAL,
        name: 'Pending Approval',
        description: 'Awaiting final approval from loan committee',
        icon: 'shield-check',
        color: '#EC4899', // Pink
        stageType: STAGE_TYPES.APPROVAL,
        order: 4,
        slaHours: 8,
        requiredFields: ['underwriting_decision', 'conditions'],
        isActive: true,
      },
      {
        id: PIPELINE_STAGE_IDS.LOAN_CLEAR_TO_CLOSE,
        name: 'Approved - Clear to Close',
        description: 'Loan approved and ready for closing',
        icon: 'check-circle',
        color: '#10B981', // Green
        stageType: STAGE_TYPES.NORMAL,
        order: 5,
        slaHours: 48,
        isActive: true,
      },
      {
        id: PIPELINE_STAGE_IDS.LOAN_FUNDED,
        name: 'Funded',
        description: 'Loan has been funded successfully',
        icon: 'banknotes',
        color: '#059669', // Emerald
        stageType: STAGE_TYPES.SUCCESS,
        order: 6,
        isActive: true,
      },
      {
        id: PIPELINE_STAGE_IDS.LOAN_DENIED,
        name: 'Denied',
        description: 'Loan application was denied',
        icon: 'x-circle',
        color: '#EF4444', // Red
        stageType: STAGE_TYPES.FAILURE,
        order: 7,
        isActive: true,
      },
      {
        id: PIPELINE_STAGE_IDS.LOAN_WITHDRAWN,
        name: 'Withdrawn',
        description: 'Application withdrawn by borrower',
        icon: 'arrow-uturn-left',
        color: '#6B7280', // Gray
        stageType: STAGE_TYPES.FAILURE,
        order: 8,
        isActive: true,
      },
    ],
  },

  // ==========================================================================
  // SUPPORT TICKET PIPELINE
  // ==========================================================================
  SUPPORT_TICKET: {
    id: PIPELINE_IDS.SUPPORT_TICKET,
    name: 'Support Ticket Pipeline',
    description: 'Workflow for handling customer support requests and inquiries',
    icon: 'chat-bubble-left-right',
    color: '#10B981', // Green
    entityTypes: ['support_ticket', 'case'],
    defaultSlaHours: 24,
    isActive: true,
    sortOrder: 2,
    stages: [
      {
        id: PIPELINE_STAGE_IDS.SUPPORT_NEW,
        name: 'New',
        description: 'New support ticket, awaiting triage',
        icon: 'inbox',
        color: '#3B82F6', // Blue
        stageType: STAGE_TYPES.INITIAL,
        order: 1,
        slaHours: 1,
        isActive: true,
      },
      {
        id: PIPELINE_STAGE_IDS.SUPPORT_IN_PROGRESS,
        name: 'In Progress',
        description: 'Ticket is being worked on by support team',
        icon: 'play',
        color: '#F59E0B', // Amber
        stageType: STAGE_TYPES.NORMAL,
        order: 2,
        slaHours: 4,
        isActive: true,
      },
      {
        id: PIPELINE_STAGE_IDS.SUPPORT_WAITING,
        name: 'Waiting for Customer',
        description: 'Awaiting response or information from customer',
        icon: 'clock',
        color: '#8B5CF6', // Purple
        stageType: STAGE_TYPES.WAITING,
        order: 3,
        pauseSla: true,
        isActive: true,
      },
      {
        id: PIPELINE_STAGE_IDS.SUPPORT_ESCALATED,
        name: 'Escalated',
        description: 'Ticket escalated to senior support or management',
        icon: 'arrow-trending-up',
        color: '#EF4444', // Red
        stageType: STAGE_TYPES.NORMAL,
        order: 4,
        slaHours: 2,
        isActive: true,
      },
      {
        id: PIPELINE_STAGE_IDS.SUPPORT_RESOLVED,
        name: 'Resolved',
        description: 'Issue has been resolved',
        icon: 'check-circle',
        color: '#10B981', // Green
        stageType: STAGE_TYPES.SUCCESS,
        order: 5,
        isActive: true,
      },
      {
        id: PIPELINE_STAGE_IDS.SUPPORT_CLOSED,
        name: 'Closed',
        description: 'Ticket closed after resolution confirmation',
        icon: 'archive-box',
        color: '#6B7280', // Gray
        stageType: STAGE_TYPES.SUCCESS,
        order: 6,
        isActive: true,
      },
    ],
  },

  // ==========================================================================
  // DOCUMENT REVIEW PIPELINE
  // ==========================================================================
  DOCUMENT_REVIEW: {
    id: PIPELINE_IDS.DOCUMENT_REVIEW,
    name: 'Document Review Pipeline',
    description: 'Workflow for processing and reviewing submitted documents',
    icon: 'document-magnifying-glass',
    color: '#F59E0B', // Amber
    entityTypes: ['document_request', 'case'],
    defaultSlaHours: 48,
    isActive: true,
    sortOrder: 3,
    stages: [
      {
        id: PIPELINE_STAGE_IDS.DOC_SUBMITTED,
        name: 'Submitted',
        description: 'Document submitted for review',
        icon: 'arrow-up-tray',
        color: '#3B82F6', // Blue
        stageType: STAGE_TYPES.INITIAL,
        order: 1,
        slaHours: 2,
        isActive: true,
      },
      {
        id: PIPELINE_STAGE_IDS.DOC_AI_PROCESSING,
        name: 'AI Processing',
        description: 'Document being analyzed by AI for data extraction',
        icon: 'cpu-chip',
        color: '#8B5CF6', // Purple
        stageType: STAGE_TYPES.NORMAL,
        order: 2,
        slaHours: 1,
        isActive: true,
      },
      {
        id: PIPELINE_STAGE_IDS.DOC_HUMAN_REVIEW,
        name: 'Human Review',
        description: 'Document flagged for human review',
        icon: 'eye',
        color: '#F59E0B', // Amber
        stageType: STAGE_TYPES.APPROVAL,
        order: 3,
        slaHours: 4,
        isActive: true,
      },
      {
        id: PIPELINE_STAGE_IDS.DOC_APPROVED,
        name: 'Approved',
        description: 'Document verified and approved',
        icon: 'check-badge',
        color: '#10B981', // Green
        stageType: STAGE_TYPES.SUCCESS,
        order: 4,
        isActive: true,
      },
      {
        id: PIPELINE_STAGE_IDS.DOC_REJECTED,
        name: 'Rejected',
        description: 'Document rejected - resubmission required',
        icon: 'x-circle',
        color: '#EF4444', // Red
        stageType: STAGE_TYPES.FAILURE,
        order: 5,
        isActive: true,
      },
    ],
  },

  // ==========================================================================
  // PAYMENT PROCESSING PIPELINE
  // ==========================================================================
  PAYMENT_PROCESSING: {
    id: PIPELINE_IDS.PAYMENT_PROCESSING,
    name: 'Payment Processing Pipeline',
    description: 'Workflow for handling payment issues and requests',
    icon: 'credit-card',
    color: '#EC4899', // Pink
    entityTypes: ['payment_request', 'case'],
    defaultSlaHours: 24,
    isActive: true,
    sortOrder: 4,
    stages: [
      {
        id: PIPELINE_STAGE_IDS.PAYMENT_RECEIVED,
        name: 'Received',
        description: 'Payment request or issue received',
        icon: 'inbox',
        color: '#3B82F6', // Blue
        stageType: STAGE_TYPES.INITIAL,
        order: 1,
        slaHours: 2,
        isActive: true,
      },
      {
        id: PIPELINE_STAGE_IDS.PAYMENT_UNDER_REVIEW,
        name: 'Under Review',
        description: 'Payment details being reviewed',
        icon: 'magnifying-glass',
        color: '#F59E0B', // Amber
        stageType: STAGE_TYPES.NORMAL,
        order: 2,
        slaHours: 4,
        isActive: true,
      },
      {
        id: PIPELINE_STAGE_IDS.PAYMENT_PENDING_APPROVAL,
        name: 'Pending Approval',
        description: 'Awaiting manager approval for processing',
        icon: 'shield-check',
        color: '#8B5CF6', // Purple
        stageType: STAGE_TYPES.APPROVAL,
        order: 3,
        slaHours: 4,
        isActive: true,
      },
      {
        id: PIPELINE_STAGE_IDS.PAYMENT_PROCESSING,
        name: 'Processing',
        description: 'Payment being processed',
        icon: 'arrow-path',
        color: '#EC4899', // Pink
        stageType: STAGE_TYPES.NORMAL,
        order: 4,
        slaHours: 2,
        isActive: true,
      },
      {
        id: PIPELINE_STAGE_IDS.PAYMENT_COMPLETED,
        name: 'Completed',
        description: 'Payment processed successfully',
        icon: 'check-circle',
        color: '#10B981', // Green
        stageType: STAGE_TYPES.SUCCESS,
        order: 5,
        isActive: true,
      },
      {
        id: PIPELINE_STAGE_IDS.PAYMENT_FAILED,
        name: 'Failed',
        description: 'Payment processing failed',
        icon: 'exclamation-circle',
        color: '#EF4444', // Red
        stageType: STAGE_TYPES.FAILURE,
        order: 6,
        isActive: true,
      },
    ],
  },
};

// =============================================================================
// PIPELINE AUTOMATION DATA
// =============================================================================

export const PIPELINE_AUTOMATIONS: Record<keyof typeof PIPELINE_AUTOMATION_IDS, PipelineAutomationSeedData> = {
  // Loan Application Automations
  LOAN_AUTO_ASSIGN_UNDERWRITER: {
    id: PIPELINE_AUTOMATION_IDS.LOAN_AUTO_ASSIGN_UNDERWRITER,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    pipelineId: PIPELINE_IDS.LOAN_APPLICATION,
    name: 'Auto-Assign Underwriter',
    description: 'Automatically assign underwriter when loan moves to underwriting stage',
    triggerType: AUTOMATION_TRIGGERS.STAGE_ENTERED,
    triggerConfig: { stageId: PIPELINE_STAGE_IDS.LOAN_UNDERWRITING },
    conditions: { operator: 'AND', conditions: [{ field: 'assignedTo', operator: 'eq', value: null }] },
    actions: [
      { type: 'assign_round_robin', pool: 'underwriters' },
      { type: 'send_notification', template: 'loan_assigned', recipients: ['assignee'] },
    ],
    isActive: true,
    priority: 10,
  },
  LOAN_AUTO_NOTIFY_BORROWER: {
    id: PIPELINE_AUTOMATION_IDS.LOAN_AUTO_NOTIFY_BORROWER,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    pipelineId: PIPELINE_IDS.LOAN_APPLICATION,
    name: 'Notify Borrower on Status Change',
    description: 'Send notification to borrower when loan status changes',
    triggerType: AUTOMATION_TRIGGERS.STAGE_ENTERED,
    triggerConfig: {},
    conditions: {},
    actions: [{ type: 'send_notification', template: 'loan_status_update', recipients: ['consumer'] }],
    isActive: true,
    priority: 5,
  },
  LOAN_SLA_ESCALATION: {
    id: PIPELINE_AUTOMATION_IDS.LOAN_SLA_ESCALATION,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    pipelineId: PIPELINE_IDS.LOAN_APPLICATION,
    name: 'Loan SLA Breach Escalation',
    description: 'Escalate to manager when loan SLA is breached',
    triggerType: AUTOMATION_TRIGGERS.SLA_BREACH,
    triggerConfig: { slaDefinitionId: SLA_DEFINITION_IDS.LOAN_UNDERWRITING_REVIEW },
    conditions: {},
    actions: [
      { type: 'escalate', toRole: 'manager' },
      { type: 'send_notification', template: 'sla_breach_alert', recipients: ['manager', 'assignee'] },
    ],
    isActive: true,
    priority: 20,
  },

  // Support Ticket Automations
  SUPPORT_AUTO_CATEGORIZE: {
    id: PIPELINE_AUTOMATION_IDS.SUPPORT_AUTO_CATEGORIZE,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    pipelineId: PIPELINE_IDS.SUPPORT_TICKET,
    name: 'Auto-Categorize Support Ticket',
    description: 'Use AI to automatically categorize new support tickets',
    triggerType: AUTOMATION_TRIGGERS.RECORD_CREATED,
    triggerConfig: {},
    conditions: {},
    actions: [{ type: 'ai_categorize', field: 'category', model: 'ticket-classifier' }],
    isActive: true,
    priority: 10,
  },
  SUPPORT_URGENT_ESCALATION: {
    id: PIPELINE_AUTOMATION_IDS.SUPPORT_URGENT_ESCALATION,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    pipelineId: PIPELINE_IDS.SUPPORT_TICKET,
    name: 'Urgent Ticket Escalation',
    description: 'Auto-escalate urgent priority tickets',
    triggerType: AUTOMATION_TRIGGERS.FIELD_CHANGED,
    triggerConfig: { fieldName: 'priority' },
    conditions: { operator: 'AND', conditions: [{ field: 'priority', operator: 'eq', value: 'urgent' }] },
    actions: [
      { type: 'move_to_stage', stageId: PIPELINE_STAGE_IDS.SUPPORT_ESCALATED },
      { type: 'send_notification', template: 'urgent_escalation', recipients: ['manager'] },
    ],
    isActive: true,
    priority: 15,
  },
  SUPPORT_AUTO_CLOSE_RESOLVED: {
    id: PIPELINE_AUTOMATION_IDS.SUPPORT_AUTO_CLOSE_RESOLVED,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    pipelineId: PIPELINE_IDS.SUPPORT_TICKET,
    name: 'Auto-Close Resolved Tickets',
    description: 'Automatically close tickets 7 days after resolution if no response',
    triggerType: AUTOMATION_TRIGGERS.SCHEDULED,
    triggerConfig: { cron: '0 0 * * *', daysAfterResolved: 7 },
    conditions: {
      operator: 'AND',
      conditions: [
        { field: 'stageId', operator: 'eq', value: PIPELINE_STAGE_IDS.SUPPORT_RESOLVED },
        { field: 'lastActivityDays', operator: 'gt', value: 7 },
      ],
    },
    actions: [
      { type: 'move_to_stage', stageId: PIPELINE_STAGE_IDS.SUPPORT_CLOSED },
      { type: 'send_notification', template: 'ticket_auto_closed', recipients: ['consumer'] },
    ],
    isActive: true,
    priority: 5,
  },

  // Document Review Automations
  DOC_AUTO_OCR_TRIGGER: {
    id: PIPELINE_AUTOMATION_IDS.DOC_AUTO_OCR_TRIGGER,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    pipelineId: PIPELINE_IDS.DOCUMENT_REVIEW,
    name: 'Trigger OCR Processing',
    description: 'Automatically trigger OCR when document is submitted',
    triggerType: AUTOMATION_TRIGGERS.STAGE_ENTERED,
    triggerConfig: { stageId: PIPELINE_STAGE_IDS.DOC_SUBMITTED },
    conditions: { operator: 'AND', conditions: [{ field: 'fileType', operator: 'in', value: ['pdf', 'image'] }] },
    actions: [
      { type: 'move_to_stage', stageId: PIPELINE_STAGE_IDS.DOC_AI_PROCESSING },
      { type: 'trigger_job', jobType: 'document_ocr' },
    ],
    isActive: true,
    priority: 10,
  },
  DOC_CONFIDENCE_ROUTING: {
    id: PIPELINE_AUTOMATION_IDS.DOC_CONFIDENCE_ROUTING,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    pipelineId: PIPELINE_IDS.DOCUMENT_REVIEW,
    name: 'Route Based on AI Confidence',
    description: 'Route to human review if AI confidence is below threshold',
    triggerType: AUTOMATION_TRIGGERS.STAGE_EXITED,
    triggerConfig: { stageId: PIPELINE_STAGE_IDS.DOC_AI_PROCESSING },
    conditions: { operator: 'OR', conditions: [{ field: 'aiConfidence', operator: 'lt', value: 0.85 }] },
    actions: [
      { type: 'move_to_stage', stageId: PIPELINE_STAGE_IDS.DOC_HUMAN_REVIEW },
      { type: 'update_field', field: 'requiresHumanReview', value: true },
    ],
    isActive: true,
    priority: 10,
  },

  // Payment Processing Automations
  PAYMENT_LARGE_AMOUNT_APPROVAL: {
    id: PIPELINE_AUTOMATION_IDS.PAYMENT_LARGE_AMOUNT_APPROVAL,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    pipelineId: PIPELINE_IDS.PAYMENT_PROCESSING,
    name: 'Large Payment Approval Required',
    description: 'Require manager approval for payments over $50,000',
    triggerType: AUTOMATION_TRIGGERS.RECORD_CREATED,
    triggerConfig: {},
    conditions: { operator: 'AND', conditions: [{ field: 'amount', operator: 'gt', value: 50000 }] },
    actions: [
      { type: 'move_to_stage', stageId: PIPELINE_STAGE_IDS.PAYMENT_PENDING_APPROVAL },
      { type: 'assign', toRole: 'finance_manager' },
      { type: 'send_notification', template: 'large_payment_approval', recipients: ['finance_manager'] },
    ],
    isActive: true,
    priority: 15,
  },
  PAYMENT_RETRY_FAILED: {
    id: PIPELINE_AUTOMATION_IDS.PAYMENT_RETRY_FAILED,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    pipelineId: PIPELINE_IDS.PAYMENT_PROCESSING,
    name: 'Auto-Retry Failed Payments',
    description: 'Automatically retry failed ACH payments up to 3 times',
    triggerType: AUTOMATION_TRIGGERS.STAGE_ENTERED,
    triggerConfig: { stageId: PIPELINE_STAGE_IDS.PAYMENT_FAILED },
    conditions: { operator: 'AND', conditions: [{ field: 'retryCount', operator: 'lt', value: 3 }] },
    actions: [
      { type: 'wait', duration: '24h' },
      { type: 'increment_field', field: 'retryCount' },
      { type: 'move_to_stage', stageId: PIPELINE_STAGE_IDS.PAYMENT_PROCESSING },
      { type: 'trigger_job', jobType: 'payment_retry' },
    ],
    isActive: true,
    priority: 10,
  },
};

// =============================================================================
// SLA DEFINITION DATA
// =============================================================================

export const SLA_DEFINITIONS: Record<keyof typeof SLA_DEFINITION_IDS, SLADefinitionSeedData> = {
  // Loan SLAs
  LOAN_INITIAL_REVIEW: {
    id: SLA_DEFINITION_IDS.LOAN_INITIAL_REVIEW,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'Loan Initial Review SLA',
    description: 'Time to complete initial review of loan application',
    entityTypes: ['loan_application', 'case'],
    pipelineId: PIPELINE_IDS.LOAN_APPLICATION,
    stageId: PIPELINE_STAGE_IDS.LOAN_NEW_APPLICATION,
    metricType: SLA_METRIC_TYPES.TIME_IN_STAGE,
    targetValue: 240, // 4 hours in minutes
    warningThreshold: 80,
    criticalThreshold: 95,
    useBusinessHours: true,
    priority: 1,
    isActive: true,
  },
  LOAN_UNDERWRITING_REVIEW: {
    id: SLA_DEFINITION_IDS.LOAN_UNDERWRITING_REVIEW,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'Loan Underwriting Review SLA',
    description: 'Time to complete underwriting review',
    entityTypes: ['loan_application'],
    pipelineId: PIPELINE_IDS.LOAN_APPLICATION,
    stageId: PIPELINE_STAGE_IDS.LOAN_UNDERWRITING,
    metricType: SLA_METRIC_TYPES.TIME_IN_STAGE,
    targetValue: 1440, // 24 hours in minutes
    warningThreshold: 80,
    criticalThreshold: 95,
    useBusinessHours: true,
    priority: 1,
    isActive: true,
  },
  LOAN_APPROVAL_TURNAROUND: {
    id: SLA_DEFINITION_IDS.LOAN_APPROVAL_TURNAROUND,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'Loan Approval Turnaround SLA',
    description: 'Time for loan committee to approve/deny',
    entityTypes: ['loan_application'],
    pipelineId: PIPELINE_IDS.LOAN_APPLICATION,
    stageId: PIPELINE_STAGE_IDS.LOAN_PENDING_APPROVAL,
    metricType: SLA_METRIC_TYPES.TIME_IN_STAGE,
    targetValue: 480, // 8 hours in minutes
    warningThreshold: 75,
    criticalThreshold: 90,
    useBusinessHours: true,
    priority: 2,
    isActive: true,
  },
  LOAN_TOTAL_PROCESSING: {
    id: SLA_DEFINITION_IDS.LOAN_TOTAL_PROCESSING,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'Total Loan Processing SLA',
    description: 'Total time from application to funding decision',
    entityTypes: ['loan_application'],
    pipelineId: PIPELINE_IDS.LOAN_APPLICATION,
    metricType: SLA_METRIC_TYPES.TIME_TO_COMPLETION,
    targetValue: 4320, // 72 hours in minutes
    warningThreshold: 80,
    criticalThreshold: 95,
    useBusinessHours: false, // Calendar time
    priority: 0,
    isActive: true,
  },

  // Support SLAs
  SUPPORT_FIRST_RESPONSE: {
    id: SLA_DEFINITION_IDS.SUPPORT_FIRST_RESPONSE,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'Support First Response SLA',
    description: 'Time to first response on support ticket',
    entityTypes: ['support_ticket', 'case'],
    pipelineId: PIPELINE_IDS.SUPPORT_TICKET,
    metricType: SLA_METRIC_TYPES.FIRST_RESPONSE_TIME,
    targetValue: 60, // 1 hour in minutes
    warningThreshold: 75,
    criticalThreshold: 90,
    useBusinessHours: true,
    priority: 1,
    isActive: true,
  },
  SUPPORT_RESOLUTION_TIME: {
    id: SLA_DEFINITION_IDS.SUPPORT_RESOLUTION_TIME,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'Support Resolution SLA',
    description: 'Total time to resolve support ticket',
    entityTypes: ['support_ticket'],
    pipelineId: PIPELINE_IDS.SUPPORT_TICKET,
    metricType: SLA_METRIC_TYPES.TOTAL_RESOLUTION_TIME,
    targetValue: 1440, // 24 hours in minutes
    warningThreshold: 80,
    criticalThreshold: 95,
    useBusinessHours: true,
    priority: 0,
    isActive: true,
  },
  SUPPORT_ESCALATION_RESPONSE: {
    id: SLA_DEFINITION_IDS.SUPPORT_ESCALATION_RESPONSE,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'Escalation Response SLA',
    description: 'Time to respond to escalated tickets',
    entityTypes: ['support_ticket'],
    pipelineId: PIPELINE_IDS.SUPPORT_TICKET,
    stageId: PIPELINE_STAGE_IDS.SUPPORT_ESCALATED,
    metricType: SLA_METRIC_TYPES.TIME_IN_STAGE,
    targetValue: 120, // 2 hours in minutes
    warningThreshold: 70,
    criticalThreshold: 85,
    useBusinessHours: true,
    priority: 2,
    isActive: true,
  },

  // Document SLAs
  DOC_AI_PROCESSING: {
    id: SLA_DEFINITION_IDS.DOC_AI_PROCESSING,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'Document AI Processing SLA',
    description: 'Time for AI to process submitted document',
    entityTypes: ['document_request'],
    pipelineId: PIPELINE_IDS.DOCUMENT_REVIEW,
    stageId: PIPELINE_STAGE_IDS.DOC_AI_PROCESSING,
    metricType: SLA_METRIC_TYPES.TIME_IN_STAGE,
    targetValue: 60, // 1 hour in minutes
    warningThreshold: 80,
    criticalThreshold: 95,
    useBusinessHours: false, // AI runs 24/7
    priority: 1,
    isActive: true,
  },
  DOC_HUMAN_REVIEW: {
    id: SLA_DEFINITION_IDS.DOC_HUMAN_REVIEW,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'Document Human Review SLA',
    description: 'Time for human to review flagged document',
    entityTypes: ['document_request'],
    pipelineId: PIPELINE_IDS.DOCUMENT_REVIEW,
    stageId: PIPELINE_STAGE_IDS.DOC_HUMAN_REVIEW,
    metricType: SLA_METRIC_TYPES.TIME_IN_STAGE,
    targetValue: 240, // 4 hours in minutes
    warningThreshold: 80,
    criticalThreshold: 95,
    useBusinessHours: true,
    priority: 1,
    isActive: true,
  },

  // Payment SLAs
  PAYMENT_PROCESSING_TIME: {
    id: SLA_DEFINITION_IDS.PAYMENT_PROCESSING_TIME,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'Payment Processing SLA',
    description: 'Total time to process payment request',
    entityTypes: ['payment_request'],
    pipelineId: PIPELINE_IDS.PAYMENT_PROCESSING,
    metricType: SLA_METRIC_TYPES.TIME_TO_COMPLETION,
    targetValue: 1440, // 24 hours in minutes
    warningThreshold: 80,
    criticalThreshold: 95,
    useBusinessHours: false, // Calendar time for payments
    priority: 0,
    isActive: true,
  },

  // ==========================================================================
  // 1SHARPE CAPITAL - CONSTRUCTION LENDING SLAs
  // ==========================================================================
  SHARPE_CONSTRUCTION_DRAW: {
    id: SLA_DEFINITION_IDS.SHARPE_CONSTRUCTION_DRAW,
    businessId: BUSINESS_IDS.ONE_SHARPE_CAPITAL,
    name: 'Construction Draw Processing',
    description: 'Time to process and fund construction draw request',
    entityTypes: ['case', 'draw_request'],
    pipelineId: PIPELINE_IDS.PAYMENT_PROCESSING,
    metricType: SLA_METRIC_TYPES.TIME_TO_COMPLETION,
    targetValue: 2880, // 48 hours in minutes
    warningThreshold: 75,
    criticalThreshold: 90,
    useBusinessHours: true,
    priority: 0,
    isActive: true,
  },
  SHARPE_INSPECTION_REVIEW: {
    id: SLA_DEFINITION_IDS.SHARPE_INSPECTION_REVIEW,
    businessId: BUSINESS_IDS.ONE_SHARPE_CAPITAL,
    name: 'Inspection Document Review',
    description: 'Time to review construction inspection reports',
    entityTypes: ['case', 'document'],
    pipelineId: PIPELINE_IDS.DOCUMENT_REVIEW,
    metricType: SLA_METRIC_TYPES.TIME_IN_STAGE,
    targetValue: 480, // 8 hours in minutes
    warningThreshold: 80,
    criticalThreshold: 95,
    useBusinessHours: true,
    priority: 1,
    isActive: true,
  },
  SHARPE_CONTRACTOR_APPROVAL: {
    id: SLA_DEFINITION_IDS.SHARPE_CONTRACTOR_APPROVAL,
    businessId: BUSINESS_IDS.ONE_SHARPE_CAPITAL,
    name: 'Contractor Approval SLA',
    description: 'Time to approve new contractor for project',
    entityTypes: ['contractor_approval'],
    pipelineId: PIPELINE_IDS.LOAN_APPLICATION,
    metricType: SLA_METRIC_TYPES.TIME_TO_COMPLETION,
    targetValue: 1440, // 24 hours in minutes
    warningThreshold: 70,
    criticalThreshold: 90,
    useBusinessHours: true,
    priority: 1,
    isActive: true,
  },

  // ==========================================================================
  // PACIFIC BRIDGE FUND - INVESTMENT SLAs
  // ==========================================================================
  PACIFIC_INVESTMENT_COMMITTEE: {
    id: SLA_DEFINITION_IDS.PACIFIC_INVESTMENT_COMMITTEE,
    businessId: BUSINESS_IDS.PACIFIC_BRIDGE_FUND,
    name: 'Investment Committee Review',
    description: 'Time for investment committee to review and decide on loan',
    entityTypes: ['case', 'loan_application'],
    pipelineId: PIPELINE_IDS.LOAN_APPLICATION,
    metricType: SLA_METRIC_TYPES.TIME_IN_STAGE,
    targetValue: 2880, // 48 hours (allows for weekly committee meetings)
    warningThreshold: 75,
    criticalThreshold: 95,
    useBusinessHours: true,
    priority: 0,
    isActive: true,
  },
  PACIFIC_TERM_SHEET_ISSUANCE: {
    id: SLA_DEFINITION_IDS.PACIFIC_TERM_SHEET_ISSUANCE,
    businessId: BUSINESS_IDS.PACIFIC_BRIDGE_FUND,
    name: 'Term Sheet Issuance',
    description: 'Time to issue term sheet after committee approval',
    entityTypes: ['case', 'term_sheet'],
    pipelineId: PIPELINE_IDS.LOAN_APPLICATION,
    metricType: SLA_METRIC_TYPES.TIME_TO_COMPLETION,
    targetValue: 480, // 8 hours in minutes
    warningThreshold: 80,
    criticalThreshold: 95,
    useBusinessHours: true,
    priority: 1,
    isActive: true,
  },
};

// =============================================================================
// SLA TRACKER DATA (Sample active trackers)
// =============================================================================

// Import case IDs for entity references
import { CASE_IDS } from './cases';

export const SLA_TRACKERS: Record<keyof typeof SLA_TRACKER_IDS, SLATrackerSeedData> = {
  MARCUS_LOAN_SLA: {
    id: SLA_TRACKER_IDS.MARCUS_LOAN_SLA,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    entityType: 'case',
    entityId: CASE_IDS.MARCUS_PORTFOLIO_EXPANSION,
    pipelineId: PIPELINE_IDS.LOAN_APPLICATION,
    currentStageId: PIPELINE_STAGE_IDS.LOAN_UNDERWRITING,
    startedAt: '2026-01-20T09:00:00Z',
    pausedDuration: 0,
    dueAt: '2026-01-21T09:00:00Z',
    status: SLA_STATUSES.ON_TRACK,
    escalationLevel: 0,
    stageHistory: [
      {
        stageId: PIPELINE_STAGE_IDS.LOAN_NEW_APPLICATION,
        enteredAt: '2026-01-20T09:00:00Z',
        exitedAt: '2026-01-20T11:00:00Z',
        duration: 7200,
      },
      {
        stageId: PIPELINE_STAGE_IDS.LOAN_DOCUMENT_COLLECTION,
        enteredAt: '2026-01-20T11:00:00Z',
        exitedAt: '2026-01-20T14:00:00Z',
        duration: 10800,
      },
      { stageId: PIPELINE_STAGE_IDS.LOAN_UNDERWRITING, enteredAt: '2026-01-20T14:00:00Z' },
    ],
  },
  DAVID_SUPPORT_SLA: {
    id: SLA_TRACKER_IDS.DAVID_SUPPORT_SLA,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    entityType: 'case',
    entityId: CASE_IDS.DAVID_ACH_FAILED,
    pipelineId: PIPELINE_IDS.SUPPORT_TICKET,
    currentStageId: PIPELINE_STAGE_IDS.SUPPORT_IN_PROGRESS,
    startedAt: '2026-01-21T08:30:00Z',
    pausedDuration: 0,
    dueAt: '2026-01-21T09:30:00Z',
    status: SLA_STATUSES.AT_RISK,
    escalationLevel: 1,
    lastEscalatedAt: '2026-01-21T09:00:00Z',
    stageHistory: [
      {
        stageId: PIPELINE_STAGE_IDS.SUPPORT_NEW,
        enteredAt: '2026-01-21T08:30:00Z',
        exitedAt: '2026-01-21T08:35:00Z',
        duration: 300,
      },
      { stageId: PIPELINE_STAGE_IDS.SUPPORT_IN_PROGRESS, enteredAt: '2026-01-21T08:35:00Z' },
    ],
  },
  JESSICA_DOC_SLA: {
    id: SLA_TRACKER_IDS.JESSICA_DOC_SLA,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    entityType: 'case',
    entityId: CASE_IDS.JESSICA_INSPECTION,
    pipelineId: PIPELINE_IDS.DOCUMENT_REVIEW,
    currentStageId: PIPELINE_STAGE_IDS.DOC_HUMAN_REVIEW,
    startedAt: '2026-01-20T16:00:00Z',
    pausedDuration: 0,
    dueAt: '2026-01-20T20:00:00Z',
    status: SLA_STATUSES.ON_TRACK,
    escalationLevel: 0,
    stageHistory: [
      {
        stageId: PIPELINE_STAGE_IDS.DOC_SUBMITTED,
        enteredAt: '2026-01-20T16:00:00Z',
        exitedAt: '2026-01-20T16:05:00Z',
        duration: 300,
      },
      {
        stageId: PIPELINE_STAGE_IDS.DOC_AI_PROCESSING,
        enteredAt: '2026-01-20T16:05:00Z',
        exitedAt: '2026-01-20T16:15:00Z',
        duration: 600,
      },
      { stageId: PIPELINE_STAGE_IDS.DOC_HUMAN_REVIEW, enteredAt: '2026-01-20T16:15:00Z' },
    ],
  },
  CHRISTOPHER_PAYMENT_SLA: {
    id: SLA_TRACKER_IDS.CHRISTOPHER_PAYMENT_SLA,
    businessId: BUSINESS_IDS.ONE_SHARPE_CAPITAL,
    entityType: 'case',
    entityId: CASE_IDS.CHRISTOPHER_DRAW,
    pipelineId: PIPELINE_IDS.PAYMENT_PROCESSING,
    currentStageId: PIPELINE_STAGE_IDS.PAYMENT_PENDING_APPROVAL,
    startedAt: '2026-01-19T10:00:00Z',
    pausedDuration: 0,
    dueAt: '2026-01-20T10:00:00Z',
    status: SLA_STATUSES.BREACHED,
    breachedAt: '2026-01-20T10:00:00Z',
    escalationLevel: 2,
    lastEscalatedAt: '2026-01-20T14:00:00Z',
    stageHistory: [
      {
        stageId: PIPELINE_STAGE_IDS.PAYMENT_RECEIVED,
        enteredAt: '2026-01-19T10:00:00Z',
        exitedAt: '2026-01-19T10:30:00Z',
        duration: 1800,
      },
      {
        stageId: PIPELINE_STAGE_IDS.PAYMENT_UNDER_REVIEW,
        enteredAt: '2026-01-19T10:30:00Z',
        exitedAt: '2026-01-19T14:00:00Z',
        duration: 12600,
      },
      { stageId: PIPELINE_STAGE_IDS.PAYMENT_PENDING_APPROVAL, enteredAt: '2026-01-19T14:00:00Z' },
    ],
  },

  // ==========================================================================
  // 1SHARPE CAPITAL SLA TRACKERS
  // ==========================================================================
  MARIA_CONSTRUCTION_SLA: {
    id: SLA_TRACKER_IDS.MARIA_CONSTRUCTION_SLA,
    businessId: BUSINESS_IDS.ONE_SHARPE_CAPITAL,
    entityType: 'case',
    entityId: CASE_IDS.MARIA_CONSTRUCTION,
    pipelineId: PIPELINE_IDS.LOAN_APPLICATION,
    currentStageId: PIPELINE_STAGE_IDS.LOAN_DOCUMENT_COLLECTION,
    startedAt: '2026-01-18T09:00:00Z',
    pausedDuration: 0,
    dueAt: '2026-01-22T17:00:00Z',
    status: SLA_STATUSES.ON_TRACK,
    escalationLevel: 0,
    stageHistory: [
      {
        stageId: PIPELINE_STAGE_IDS.LOAN_NEW_APPLICATION,
        enteredAt: '2026-01-18T09:00:00Z',
        exitedAt: '2026-01-18T14:00:00Z',
        duration: 18000,
      },
      { stageId: PIPELINE_STAGE_IDS.LOAN_DOCUMENT_COLLECTION, enteredAt: '2026-01-18T14:00:00Z' },
    ],
  },
  SHARPE_DRAW_SLA: {
    id: SLA_TRACKER_IDS.SHARPE_DRAW_SLA,
    businessId: BUSINESS_IDS.ONE_SHARPE_CAPITAL,
    entityType: 'case',
    entityId: CASE_IDS.CHRISTOPHER_PHOTOS,
    pipelineId: PIPELINE_IDS.DOCUMENT_REVIEW,
    currentStageId: PIPELINE_STAGE_IDS.DOC_AI_PROCESSING,
    startedAt: '2026-01-21T11:00:00Z',
    pausedDuration: 0,
    dueAt: '2026-01-21T19:00:00Z',
    status: SLA_STATUSES.ON_TRACK,
    escalationLevel: 0,
    stageHistory: [
      {
        stageId: PIPELINE_STAGE_IDS.DOC_SUBMITTED,
        enteredAt: '2026-01-21T11:00:00Z',
        exitedAt: '2026-01-21T11:02:00Z',
        duration: 120,
      },
      { stageId: PIPELINE_STAGE_IDS.DOC_AI_PROCESSING, enteredAt: '2026-01-21T11:02:00Z' },
    ],
  },

  // ==========================================================================
  // PACIFIC BRIDGE FUND SLA TRACKERS
  // ==========================================================================
  PACIFIC_COMMITTEE_SLA: {
    id: SLA_TRACKER_IDS.PACIFIC_COMMITTEE_SLA,
    businessId: BUSINESS_IDS.PACIFIC_BRIDGE_FUND,
    entityType: 'case',
    entityId: CASE_IDS.MARCUS_PORTFOLIO_EXPANSION, // Cross-business demo scenario
    pipelineId: PIPELINE_IDS.LOAN_APPLICATION,
    currentStageId: PIPELINE_STAGE_IDS.LOAN_PENDING_APPROVAL,
    startedAt: '2026-01-17T10:00:00Z',
    pausedDuration: 0,
    dueAt: '2026-01-22T10:00:00Z',
    status: SLA_STATUSES.AT_RISK,
    escalationLevel: 1,
    lastEscalatedAt: '2026-01-21T15:00:00Z',
    stageHistory: [
      {
        stageId: PIPELINE_STAGE_IDS.LOAN_NEW_APPLICATION,
        enteredAt: '2026-01-17T10:00:00Z',
        exitedAt: '2026-01-17T16:00:00Z',
        duration: 21600,
      },
      {
        stageId: PIPELINE_STAGE_IDS.LOAN_DOCUMENT_COLLECTION,
        enteredAt: '2026-01-17T16:00:00Z',
        exitedAt: '2026-01-18T14:00:00Z',
        duration: 79200,
      },
      {
        stageId: PIPELINE_STAGE_IDS.LOAN_UNDERWRITING,
        enteredAt: '2026-01-18T14:00:00Z',
        exitedAt: '2026-01-20T11:00:00Z',
        duration: 162000,
      },
      { stageId: PIPELINE_STAGE_IDS.LOAN_PENDING_APPROVAL, enteredAt: '2026-01-20T11:00:00Z' },
    ],
  },
};

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Get all pipelines as an array for seeding
 */
export function getAllPipelines(): PipelineSeedData[] {
  return Object.values(PIPELINES);
}

/**
 * Get a specific pipeline by ID
 */
export function getPipelineById(pipelineId: string): PipelineSeedData | undefined {
  return Object.values(PIPELINES).find((p) => p.id === pipelineId);
}

/**
 * Get all stages for a specific pipeline
 */
export function getStagesByPipeline(pipelineId: string): PipelineStageSeedData[] {
  const pipeline = getPipelineById(pipelineId);
  return pipeline?.stages ?? [];
}

/**
 * Get all stages across all pipelines (flattened)
 */
export function getAllStages(): PipelineStageSeedData[] {
  return Object.values(PIPELINES).flatMap((p) => p.stages);
}

/**
 * Get pipelines by entity type
 */
export function getPipelinesByEntityType(entityType: string): PipelineSeedData[] {
  return Object.values(PIPELINES).filter((p) => p.entityTypes.includes(entityType));
}

/**
 * Get initial stage for a pipeline
 */
export function getInitialStage(pipelineId: string): PipelineStageSeedData | undefined {
  const pipeline = getPipelineById(pipelineId);
  return pipeline?.stages.find((s) => s.stageType === STAGE_TYPES.INITIAL);
}

/**
 * Get success stages for a pipeline
 */
export function getSuccessStages(pipelineId: string): PipelineStageSeedData[] {
  const pipeline = getPipelineById(pipelineId);
  return pipeline?.stages.filter((s) => s.stageType === STAGE_TYPES.SUCCESS) ?? [];
}

/**
 * Get failure stages for a pipeline
 */
export function getFailureStages(pipelineId: string): PipelineStageSeedData[] {
  const pipeline = getPipelineById(pipelineId);
  return pipeline?.stages.filter((s) => s.stageType === STAGE_TYPES.FAILURE) ?? [];
}

/**
 * Get all pipeline automations as an array for seeding
 */
export function getAllPipelineAutomations(): PipelineAutomationSeedData[] {
  return Object.values(PIPELINE_AUTOMATIONS);
}

/**
 * Get automations for a specific pipeline
 */
export function getAutomationsByPipeline(pipelineId: string): PipelineAutomationSeedData[] {
  return Object.values(PIPELINE_AUTOMATIONS).filter((a) => a.pipelineId === pipelineId);
}

/**
 * Get automations for a specific business
 */
export function getAutomationsByBusiness(businessId: string): PipelineAutomationSeedData[] {
  return Object.values(PIPELINE_AUTOMATIONS).filter((a) => a.businessId === businessId);
}

/**
 * Get all SLA definitions as an array for seeding
 */
export function getAllSLADefinitions(): SLADefinitionSeedData[] {
  return Object.values(SLA_DEFINITIONS);
}

/**
 * Get SLA definitions for a specific pipeline
 */
export function getSLADefinitionsByPipeline(pipelineId: string): SLADefinitionSeedData[] {
  return Object.values(SLA_DEFINITIONS).filter((s) => s.pipelineId === pipelineId);
}

/**
 * Get SLA definitions for a specific business
 */
export function getSLADefinitionsByBusiness(businessId: string): SLADefinitionSeedData[] {
  return Object.values(SLA_DEFINITIONS).filter((s) => s.businessId === businessId);
}

/**
 * Get all SLA trackers as an array for seeding
 */
export function getAllSLATrackers(): SLATrackerSeedData[] {
  return Object.values(SLA_TRACKERS);
}

/**
 * Get SLA trackers for a specific business
 */
export function getSLATrackersByBusiness(businessId: string): SLATrackerSeedData[] {
  return Object.values(SLA_TRACKERS).filter((t) => t.businessId === businessId);
}

/**
 * Get SLA trackers by status
 */
export function getSLATrackersByStatus(status: SLAStatus): SLATrackerSeedData[] {
  return Object.values(SLA_TRACKERS).filter((t) => t.status === status);
}

/**
 * Get breached SLA trackers
 */
export function getBreachedSLATrackers(): SLATrackerSeedData[] {
  return getSLATrackersByStatus(SLA_STATUSES.BREACHED);
}

/**
 * Get at-risk SLA trackers
 */
export function getAtRiskSLATrackers(): SLATrackerSeedData[] {
  return getSLATrackersByStatus(SLA_STATUSES.AT_RISK);
}

// =============================================================================
// E2E PIPELINE SEED IDs (Issue #283 - Pipeline CRUD E2E Scenarios)
// =============================================================================

/**
 * E2E Pipeline Seed IDs
 * Stable IDs for E2E testing of Pipeline CRUD operations
 * @see Issue #283 for Pipeline E2E scenarios
 */
export const E2E_PIPELINE_SEED_IDS = {
  // -------------------------------------------------------------------------
  // Pipelines - for E2E pipeline CRUD testing
  // -------------------------------------------------------------------------
  /** Loan Application Pipeline - primary test pipeline */
  PIPELINE_LOAN: PIPELINE_IDS.LOAN_APPLICATION,
  /** Support Ticket Pipeline - secondary test pipeline */
  PIPELINE_SUPPORT: PIPELINE_IDS.SUPPORT_TICKET,
  /** Document Review Pipeline - for testing different entity types */
  PIPELINE_DOCUMENT: PIPELINE_IDS.DOCUMENT_REVIEW,
  /** Payment Processing Pipeline - for payment workflow testing */
  PIPELINE_PAYMENT: PIPELINE_IDS.PAYMENT_PROCESSING,
  /** Test pipeline for CRUD operations (created/deleted during tests) */
  PIPELINE_TEST: seedId('e2e-pipeline-test', 1),

  // -------------------------------------------------------------------------
  // Pipeline Stages - for E2E stage CRUD and reorder testing
  // -------------------------------------------------------------------------
  /** Initial stage for loan pipeline */
  STAGE_LOAN_INITIAL: PIPELINE_STAGE_IDS.LOAN_NEW_APPLICATION,
  /** Document collection stage - tests waiting/pause SLA */
  STAGE_LOAN_DOCS: PIPELINE_STAGE_IDS.LOAN_DOCUMENT_COLLECTION,
  /** Underwriting stage - tests normal workflow */
  STAGE_LOAN_UNDERWRITING: PIPELINE_STAGE_IDS.LOAN_UNDERWRITING,
  /** Approval stage - tests approval workflow */
  STAGE_LOAN_APPROVAL: PIPELINE_STAGE_IDS.LOAN_PENDING_APPROVAL,
  /** Success stage - tests completion */
  STAGE_LOAN_FUNDED: PIPELINE_STAGE_IDS.LOAN_FUNDED,
  /** Failure stage - tests failure paths */
  STAGE_LOAN_DENIED: PIPELINE_STAGE_IDS.LOAN_DENIED,
  /** Test stage for CRUD operations (created/deleted during tests) */
  STAGE_TEST: seedId('e2e-stage-test', 1),

  // -------------------------------------------------------------------------
  // Pipeline Automations - for E2E automation CRUD testing
  // -------------------------------------------------------------------------
  /** Auto-assign underwriter automation */
  AUTOMATION_ASSIGN: PIPELINE_AUTOMATION_IDS.LOAN_AUTO_ASSIGN_UNDERWRITER,
  /** Notify borrower automation */
  AUTOMATION_NOTIFY: PIPELINE_AUTOMATION_IDS.LOAN_AUTO_NOTIFY_BORROWER,
  /** SLA escalation automation */
  AUTOMATION_ESCALATION: PIPELINE_AUTOMATION_IDS.LOAN_SLA_ESCALATION,
  /** Test automation for CRUD operations (created/deleted during tests) */
  AUTOMATION_TEST: seedId('e2e-automation-test', 1),

  // -------------------------------------------------------------------------
  // SLA Definitions - for E2E SLA config testing
  // -------------------------------------------------------------------------
  /** Initial review SLA */
  SLA_INITIAL_REVIEW: SLA_DEFINITION_IDS.LOAN_INITIAL_REVIEW,
  /** Underwriting SLA */
  SLA_UNDERWRITING: SLA_DEFINITION_IDS.LOAN_UNDERWRITING_REVIEW,
  /** Approval SLA */
  SLA_APPROVAL: SLA_DEFINITION_IDS.LOAN_APPROVAL_TURNAROUND,
  /** Total processing SLA */
  SLA_TOTAL: SLA_DEFINITION_IDS.LOAN_TOTAL_PROCESSING,
} as const;

export type E2EPipelineSeedId = (typeof E2E_PIPELINE_SEED_IDS)[keyof typeof E2E_PIPELINE_SEED_IDS];
